import { type SubmitEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AlertCircle, ArrowLeft, KeyRound, LogIn } from 'lucide-react';
import { useVerifyMagicLink, useVerifyOTP } from '../hooks';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';

interface MagicLinkVerifyProps {
  onSuccess?: () => void;
}

export function MagicLinkVerify({ onSuccess }: MagicLinkVerifyProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';
  const [code, setCode] = useState('');
  const [manualOTPFallback, setManualOTPFallback] = useState(false);

  const handleVerifySuccess = () => {
    onSuccess?.();
    navigate('/portal/select-investor');
  };

  const {
    mutate: verifyToken,
    isPending: isTokenPending,
    isError: isTokenError,
    error: tokenError,
  } = useVerifyMagicLink(handleVerifySuccess);

  const {
    mutate: verifyOTP,
    isPending: isOTPPending,
    isError: isOTPError,
    error: otpError,
  } = useVerifyOTP(handleVerifySuccess);

  const showOTPFallback = manualOTPFallback || isTokenError;
  const showTokenConfirm = token && !showOTPFallback;

  const handleConfirmLogin = () => {
    if (!token) return;
    verifyToken(token);
  };

  const handleOTPSubmit = (e: SubmitEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !email) return;
    verifyOTP({ email, code });
  };

  const tokenErrorMessage =
    tokenError instanceof Error
      ? tokenError.message
      : 'Login link is invalid or expired';

  const otpErrorMessage =
    otpError instanceof Error ? otpError.message : 'An error occurred';

  if (!email) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-foreground">Dff Flow</h1>
        </div>

        <div className="w-full max-w-md border border-border bg-card rounded-2xl p-8">
          <div className="text-center space-y-6">
            <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-foreground tracking-tight">
              Invalid link
            </h1>
            <p className="text-muted-foreground leading-relaxed">
              This login link is invalid or missing required information.
            </p>
          </div>

          <button
            onClick={() => navigate('/portal/login')}
            className="mt-8 w-full py-4 font-bold text-muted-foreground border border-border rounded-full hover:text-foreground hover:border-foreground/40 transition-all"
          >
            Request new login link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-foreground">Dff Flow</h1>
      </div>

      <div className="w-full max-w-md border border-border bg-card rounded-2xl p-8">
        <div className="text-center space-y-3 mb-8">
          {showTokenConfirm ? (
            <>
              <h2 className="text-2xl font-semibold text-foreground">
                Confirm your login
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Click below to sign in as{' '}
                <span className="font-medium text-foreground">{email}</span>
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif text-3xl md:text-4xl text-foreground tracking-tight">
                Enter your login code
              </h1>
              <p className="text-muted-foreground leading-relaxed">
                Enter the 6-digit code sent to{' '}
                <span className="font-medium text-foreground">{email}</span>
              </p>
            </>
          )}
        </div>

        {showTokenConfirm ? (
          <>
            <button
              onClick={handleConfirmLogin}
              disabled={isTokenPending}
              className={buttonVariants({ variant: 'portal', size: 'portal' })}
            >
              {isTokenPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  Confirm Login
                </>
              )}
            </button>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setManualOTPFallback(true)}
                className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
              >
                Having trouble? Enter the 6-digit code from your email instead.
              </button>
            </div>
          </>
        ) : (
          <>
            {isTokenError && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3 mb-4">
                <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{tokenErrorMessage}</p>
              </div>
            )}

            <form onSubmit={handleOTPSubmit} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="000000"
                maxLength={6}
                className="w-full border border-input bg-background rounded-xl py-4 px-5 text-center text-2xl tracking-[0.3em] font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors"
                disabled={isOTPPending}
                autoFocus
              />

              {isOTPError && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{otpErrorMessage}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={code.length !== 6 || isOTPPending}
                className={buttonVariants({
                  variant: 'portal',
                  size: 'portal',
                })}
              >
                {isOTPPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-5 w-5" />
                    Verify code
                  </>
                )}
              </button>
            </form>
          </>
        )}

        <div className="mt-4 text-center">
          <Button
            variant="outline"
            onClick={() => navigate('/portal/login')}
            className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            Login codes expire after 15 minutes for security.
            <br />
            Contact your fund manager if you continue to have issues.
          </p>
        </div>
      </div>
    </div>
  );
}
